import {
  type Client,
  type Cohort,
  DataTypeSystem,
  DataTypeXceed,
} from '@efdigital/ll-addon-data';
import { findClient } from '@efdigital/ll-admin-v2/data/builders/system/clients';
import { findCohort } from '@efdigital/ll-admin-v2/data/builders/xceed/cohorts';
import { validate as validateUuid } from '@efdigital/ll-admin-v2/lib/media/paths';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

export type SpecialFolder = {
  folderId: string;
  type: DataTypeSystem.Client | DataTypeXceed.Cohort | null;
  itemId: string;
};

export interface FolderTitleSignature {
  Args: {
    folderId: string | null;
  };
  Blocks: {
    default: [title: string];
  };
}

export class FolderTitle extends Component<FolderTitleSignature> {
  @service declare store: Store;

  get specialFolder(): SpecialFolder | null {
    const id = this.args.folderId;

    if (!id) {
      return null;
    }

    // Pattern: |<guid> = client folder
    // Pattern: |<guid>|Cohorts|<guid> = cohort folder

    if (!id || id.length <= 1) {
      return null;
    }

    const parts = id.substring(1).split('|');

    // Check for client folder: |<guid>
    if (parts.length === 1 && parts[0] && validateUuid(parts[0])) {
      return { folderId: id, type: DataTypeSystem.Client, itemId: parts[0] };
    }

    // Check for cohort folder: |<guid>|Cohorts|<guid>
    if (
      parts.length === 3 &&
      parts[0] &&
      validateUuid(parts[0]) &&
      parts[1] === 'Cohorts' &&
      parts[2] &&
      validateUuid(parts[2])
    ) {
      return { folderId: id, type: DataTypeXceed.Cohort, itemId: parts[2] };
    }

    return null;
  }

  specialFolderTitle = async (
    specialFolder: SpecialFolder
  ): Promise<string> => {
    switch (specialFolder.type) {
      case DataTypeSystem.Client: {
        const title = this.store.peekRecord<Client>(
          DataTypeSystem.Client,
          specialFolder.itemId
        )?.title;
        if (title) {
          return title;
        }
        break;
      }
      case DataTypeXceed.Cohort: {
        const title = this.store.peekRecord<Cohort>(
          DataTypeXceed.Cohort,
          specialFolder.itemId
        )?.title;
        if (title) {
          return title;
        }
        break;
      }
      default: {
        return this.defaultName;
      }
    }

    try {
      const response = await this.store.request(
        specialFolder.type === DataTypeSystem.Client
          ? findClient(specialFolder.itemId)
          : findCohort(specialFolder.itemId)
      );
      return response.content.data.title;
    } catch (error) {
      console.error(error);
      return this.defaultName;
    }
  };

  get defaultName() {
    if (this.args.folderId === null) {
      return 'Files';
    }
    if (this.args.folderId.length <= 1) {
      return 'Files';
    }
    return this.args.folderId.substring(
      this.args.folderId.lastIndexOf('|') + 1
    );
  }

  <template>
    {{#let this.specialFolder as |specialFolder|}}{{#if specialFolder}}<Await
          @promise={{this.specialFolderTitle specialFolder}}
        ><:success as |title|>{{yield title}}</:success></Await>{{else}}{{yield
          this.defaultName
        }}{{/if}}{{/let}}
  </template>
}
